<template>
    <Head title="View Notification & Routing Policy" />

    <div class="py-6">
        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <!-- Header -->
            <div class="mb-6">
                <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-4">
                        <Link
                            :href="route('settings.maintenance.notification-policies.index')"
                            class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        >
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                            </svg>
                        </Link>
                        <div class="flex items-center gap-3">
                            <div>
                                <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">{{ policy.name }}</h1>
                                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">View notification and routing policy details</p>
                            </div>
                            <InfoButton title="Notification & Routing Policy Details" variant="default">
                                <p>
                                    <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                    This page displays complete details about a notification and routing policy including its matching criteria
                                    (work order type, category, priority), recipient list (users or roles), and specificity score.
                                    The specificity score determines priority when multiple policies match - higher scores mean more
                                    specific criteria and take precedence. You can activate/deactivate or delete the policy from here.
                                </p>
                                <p>
                                    <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                    • <strong>Status Badge:</strong> Green "Active" means policy sends notifications; gray "Inactive"
                                      means it's paused<br>
                                    • <strong>Matching Criteria:</strong> Shows work order conditions that trigger this policy -
                                      "All" means no restriction<br>
                                    • <strong>Specificity Score:</strong> Higher = more specific. Score 0 = default policy (matches
                                      everything)<br>
                                    • <strong>Recipients List:</strong> Shows all users or roles that receive notifications<br>
                                    • <strong>Edit Policy:</strong> Modify criteria or recipients<br>
                                    • <strong>Activate/Deactivate:</strong> Toggle notification sending without deleting<br>
                                    • <strong>Delete Policy:</strong> Permanently remove - cannot be undone
                                </p>
                                <p>
                                    <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                    • <strong>Specificity explained</strong> - Each criterion adds to the score: Type=1, Category=2,
                                      Priority=1. Policy with Category+Priority (score 3) wins over Priority only (score 1)<br>
                                    • <strong>Role recipients are dynamic</strong> - Users are determined when notifications are sent,
                                      not when policy was created<br>
                                    • <strong>Test policy matching</strong> - Create a work order matching the criteria to verify this
                                      policy is selected<br>
                                    • <strong>Deactivate temporarily</strong> - If a policy is causing issues, deactivate it instead
                                      of deleting to preserve configuration<br>
                                    • <strong>Check duplicate policies</strong> - Multiple policies with same criteria will have
                                      identical specificity scores
                                </p>
                            </InfoButton>
                        </div>
                    </div>
                    <div class="flex items-center space-x-3">
                        <Link
                            :href="route('settings.maintenance.notification-policies.edit', policy.id)"
                            class="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 dark:hover:bg-blue-600 focus:bg-blue-700 active:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition ease-in-out duration-150"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit Policy
                        </Link>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <!-- Main Content -->
                <div class="lg:col-span-2 space-y-6">
                    <!-- Policy Details Card -->
                    <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
                        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                            <h2 class="text-lg font-medium text-gray-900 dark:text-white">Policy Details</h2>
                        </div>
                        <div class="px-6 py-6 space-y-6">
                            <!-- Name -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Policy Name</label>
                                <p class="text-base text-gray-900 dark:text-white">{{ policy.name }}</p>
                            </div>

                            <!-- Description -->
                            <div v-if="policy.description">
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
                                <p class="text-base text-gray-900 dark:text-white whitespace-pre-wrap">{{ policy.description }}</p>
                            </div>

                            <!-- Status -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                                <span
                                    :class="[
                                        'px-3 py-1 inline-flex text-sm font-semibold rounded-full',
                                        policy.is_active
                                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                                    ]"
                                >
                                    {{ policy.is_active ? 'Active' : 'Inactive' }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Matching Criteria Card -->
                    <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
                        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                            <h2 class="text-lg font-medium text-gray-900 dark:text-white">Matching Criteria</h2>
                        </div>
                        <div class="px-6 py-6">
                            <div class="space-y-4">
                                <!-- Work Order Type -->
                                <div class="flex items-start">
                                    <div class="flex-shrink-0">
                                        <svg class="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                        </svg>
                                    </div>
                                    <div class="ml-3">
                                        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Work Order Type</p>
                                        <p class="text-sm text-gray-900 dark:text-white mt-1">
                                            {{ policy.work_order_type ? formatEnumValue(policy.work_order_type) : 'All types' }}
                                        </p>
                                    </div>
                                </div>

                                <!-- Service Category -->
                                <div class="flex items-start">
                                    <div class="flex-shrink-0">
                                        <svg class="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                        </svg>
                                    </div>
                                    <div class="ml-3">
                                        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Service Category</p>
                                        <p class="text-sm text-gray-900 dark:text-white mt-1">
                                            {{ policy.service_category ? policy.service_category.name : 'All categories' }}
                                        </p>
                                    </div>
                                </div>

                                <!-- Service -->
                                <div class="flex items-start">
                                    <div class="flex-shrink-0">
                                        <svg class="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </div>
                                    <div class="ml-3">
                                        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Service</p>
                                        <p class="text-sm text-gray-900 dark:text-white mt-1">
                                            {{ policy.service ? policy.service.name : 'All services' }}
                                        </p>
                                    </div>
                                </div>

                                <!-- Location -->
                                <div class="flex items-start">
                                    <div class="flex-shrink-0">
                                        <svg class="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </div>
                                    <div class="ml-3">
                                        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Location</p>
                                        <p class="text-sm text-gray-900 dark:text-white mt-1">
                                            {{ policy.location ? policy.location.name : 'All locations' }}
                                        </p>
                                    </div>
                                </div>

                                <!-- Asset -->
                                <div v-if="$page.props.subscriptionFeatures.asset_module" class="flex items-start">
                                    <div class="flex-shrink-0">
                                        <svg class="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                        </svg>
                                    </div>
                                    <div class="ml-3">
                                        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Asset</p>
                                        <p class="text-sm text-gray-900 dark:text-white mt-1">
                                            {{ policy.asset ? policy.asset.name : 'All assets' }}
                                        </p>
                                    </div>
                                </div>

                                <!-- Priority -->
                                <div class="flex items-start">
                                    <div class="flex-shrink-0">
                                        <svg class="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                    <div class="ml-3">
                                        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Priority</p>
                                        <p class="text-sm text-gray-900 dark:text-white mt-1">
                                            {{ policy.priority ? formatEnumValue(policy.priority) : 'All priorities' }}
                                        </p>
                                    </div>
                                </div>

                                <!-- Specificity Score -->
                                <div class="flex items-start">
                                    <div class="flex-shrink-0">
                                        <svg class="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                        </svg>
                                    </div>
                                    <div class="ml-3">
                                        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Specificity Score</p>
                                        <p class="text-sm text-gray-900 dark:text-white mt-1">
                                            {{ policy.specificity_score }} ({{ policy.specificity_score === 0 ? 'Default policy' : 'Higher score = more specific' }})
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <!-- Info Note -->
                            <div class="mt-6 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-500 rounded-lg p-4">
                                <p class="text-sm text-blue-700 dark:text-blue-300">
                                    <strong>Note:</strong> {{ policy.criteria_description }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Recipients Card -->
                    <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
                        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                            <h2 class="text-lg font-medium text-gray-900 dark:text-white">Recipients</h2>
                        </div>
                        <div class="px-6 py-6">
                            <div class="mb-4">
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Recipient Type</label>
                                <div class="flex items-center">
                                    <svg
                                        v-if="policy.recipient_type === 'users'"
                                        class="w-5 h-5 text-blue-500 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    <svg
                                        v-else
                                        class="w-5 h-5 text-purple-500 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                    <span class="text-base font-medium text-gray-900 dark:text-white">
                                        {{ policy.recipient_type === 'users' ? 'Individual Users' : 'Roles' }}
                                    </span>
                                    <span class="ml-2 text-sm text-gray-500 dark:text-gray-400">({{ policy.recipient_count }} total)</span>
                                </div>
                            </div>

                            <!-- Recipients List -->
                            <div class="mt-4">
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                    {{ policy.recipient_type === 'users' ? 'Selected Users' : 'Selected Roles' }}
                                </label>
                                <div class="space-y-2">
                                    <div
                                        v-for="recipient in policy.recipients"
                                        :key="recipient.id"
                                        class="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                                    >
                                        <div class="flex-shrink-0">
                                            <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                                                <span class="text-white font-semibold text-sm">
                                                    {{ recipient.name.charAt(0).toUpperCase() }}
                                                </span>
                                            </div>
                                        </div>
                                        <div class="ml-3 flex-1">
                                            <p class="text-sm font-medium text-gray-900 dark:text-white">{{ recipient.name }}</p>
                                            <p v-if="policy.recipient_type === 'users' && recipient.email" class="text-xs text-gray-500 dark:text-gray-400">
                                                {{ recipient.email }}
                                            </p>
                                            <p v-if="policy.recipient_type === 'roles' && recipient.description" class="text-xs text-gray-500 dark:text-gray-400">
                                                {{ recipient.description }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Work Order Routing Card -->
                    <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
                        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                            <h2 class="text-lg font-medium text-gray-900 dark:text-white">Work Order Routing</h2>
                        </div>
                        <div class="px-6 py-6">
                            <div class="mb-4">
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Routing Configuration</label>
                                <div class="flex items-center">
                                    <svg class="w-5 h-5 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                    </svg>
                                    <span class="text-base font-medium text-gray-900 dark:text-white">
                                        {{ policy.routing_count > 0 ? 'Specific Users' : 'All Maintenance Support Managers' }}
                                    </span>
                                    <span v-if="policy.routing_count > 0" class="ml-2 text-sm text-gray-500 dark:text-gray-400">({{ policy.routing_count }} user{{ policy.routing_count !== 1 ? 's' : '' }})</span>
                                </div>
                            </div>

                            <!-- Routing Info -->
                            <div v-if="policy.routing_count === 0" class="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-500 rounded-lg p-4">
                                <p class="text-sm text-blue-700 dark:text-blue-300">
                                    <strong>Note:</strong> No specific routing configured. All users with the Maintenance Support Manager role can view and action work orders matching this policy.
                                </p>
                            </div>

                            <!-- Routing Users List -->
                            <div v-else class="mt-4">
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                    Routed to These Users
                                </label>
                                <div class="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-500 rounded-lg p-3 mb-4">
                                    <p class="text-sm text-yellow-700 dark:text-yellow-300">
                                        <strong>Note:</strong> Only these specific users with the Maintenance Support Manager role will be able to view and action work orders matching this policy. Other users with the same role will not see these work orders.
                                    </p>
                                </div>
                                <div class="space-y-2">
                                    <div
                                        v-for="user in policy.routing_users"
                                        :key="user.id"
                                        class="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                                    >
                                        <div class="flex-shrink-0">
                                            <div class="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                                                <span class="text-white font-semibold text-sm">
                                                    {{ user.name.charAt(0).toUpperCase() }}
                                                </span>
                                            </div>
                                        </div>
                                        <div class="ml-3 flex-1">
                                            <p class="text-sm font-medium text-gray-900 dark:text-white">{{ user.name }}</p>
                                            <p class="text-xs text-gray-500 dark:text-gray-400">{{ user.email }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Sidebar -->
                <div class="lg:col-span-1 space-y-6">
                    <!-- Meta Information Card -->
                    <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
                        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                            <h2 class="text-lg font-medium text-gray-900 dark:text-white">Information</h2>
                        </div>
                        <div class="px-6 py-6 space-y-4">
                            <div>
                                <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</p>
                                <p class="mt-1 text-sm text-gray-900 dark:text-white">{{ policy.created_at }}</p>
                            </div>
                            <div>
                                <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Updated</p>
                                <p class="mt-1 text-sm text-gray-900 dark:text-white">{{ policy.updated_at }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Actions Card -->
                    <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
                        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                            <h2 class="text-lg font-medium text-gray-900 dark:text-white">Actions</h2>
                        </div>
                        <div class="px-6 py-6 space-y-3">
                            <button
                                @click="toggleActive"
                                class="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                </svg>
                                {{ policy.is_active ? 'Deactivate Policy' : 'Activate Policy' }}
                            </button>

                            <button
                                @click="deletePolicy"
                                class="w-full inline-flex items-center justify-center px-4 py-2 border border-red-300 dark:border-red-500 rounded-md shadow-sm text-sm font-medium text-red-700 dark:text-red-400 bg-white dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete Policy
                            </button>
                        </div>
                    </div>

                    <!-- How It Works Card -->
                    <div class="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-500 rounded-lg p-4">
                        <h3 class="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">How It Works</h3>
                        <p class="text-xs text-blue-700 dark:text-blue-300">
                            When a work order matching this policy's criteria is created, all recipients will receive:
                        </p>
                        <ul class="mt-2 text-xs text-blue-700 dark:text-blue-300 space-y-1 list-disc list-inside">
                            <li>Email notification</li>
                            <li>In-app notification</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { Head, Link, router } from '@inertiajs/vue3';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    policy: Object,
});

const toast = useToast();

const formatEnumValue = (value) => {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
};

const toggleActive = () => {
    router.post(
        route('settings.maintenance.notification-policies.toggleActive', props.policy.id),
        {},
        {
            preserveScroll: true,
            onSuccess: () => {
                toast.success(`Policy ${props.policy.is_active ? 'deactivated' : 'activated'} successfully`);
            },
            onError: () => {
                toast.error('Failed to update policy status');
            },
        }
    );
};

const deletePolicy = () => {
    if (!confirm(`Are you sure you want to delete "${props.policy.name}"? This action cannot be undone.`)) {
        return;
    }

    router.delete(route('settings.maintenance.notification-policies.destroy', props.policy.id), {
        onSuccess: () => {
            toast.success('Policy deleted successfully');
            router.visit(route('settings.maintenance.notification-policies.index'));
        },
        onError: (errors) => {
            toast.error(errors.error || 'Failed to delete policy');
        },
    });
};
</script>
